import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import EntityDialog from "@/components/EntityDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Users as UsersIcon, Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/lib/useOptions";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Users() {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const roles = useOptions("/roles", "name", "name");

  const roleOptions = roles.map((r) => ({ value: r.value, label: r.label.replace("_", " ") }));

  const fields = [
    { name: "name", label: "Full Name", required: true },
    { name: "email", label: "Email", required: true },
    ...(!editing ? [{ name: "password", label: "Password", placeholder: "Default: Welcome@123" }] : []),
    { name: "role", label: "Role", type: "select", required: true, options: roleOptions },
    { name: "status", label: "Status", type: "select", options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
    { name: "phone", label: "Phone" },
  ];

  const del = async (row, reload) => {
    if (!window.confirm(`Deactivate ${row.name}?`)) return;
    try { await api.delete(`/users/${row.id}`); toast.success("Deactivated"); reload(); } catch { toast.error("Failed"); }
  };

  const cols = [
    { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "email", label: "Email", mono: true },
    { key: "role", label: "Role", render: (r) => <span className="capitalize">{(r.role || "").replace("_", " ")}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "created_at", label: "Joined", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ];

  return (
    <div>
      <PageHeader title="Users" subtitle="Team members, roles & access status" icon={UsersIcon}
        breadcrumb={["Home", "Administration", "Users"]}
        actions={can("users.create") && (
          <Button data-testid="add-user-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1.5" />Add User
          </Button>
        )} />
      <DataTable endpoint="/users" columns={cols} testId="users-table" refreshKey={refresh}
        actions={(row, reload) => (
          <div className="flex items-center justify-end gap-1">
            {can("users.update") && <button data-testid={`edit-user-${row.id}`} onClick={() => { setEditing(row); setOpen(true); }} className="p-2 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>}
            {can("users.delete") && <button data-testid={`delete-user-${row.id}`} onClick={() => del(row, reload)} className="p-2 rounded-md hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>}
          </div>
        )} />
      <EntityDialog open={open} onOpenChange={setOpen} title={editing ? "Edit User" : "New User"}
        fields={fields} endpoint="/users" initial={editing} onSaved={() => setRefresh((r) => r + 1)} />
    </div>
  );
}
